import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { PriorityEnum } from '@application/enums/priority-enum';
import { TaskCompletionStatusEnum } from '@application/enums/task-completion-status-enum';
import { deleteTaskAsync, updateTaskAsync } from '@application/features/task/taskThunk';
import { hasPermission } from '@application/helpers/permission-helper';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Icon } from '@iconify/react';
import { Avatar, Box, Button, Checkbox, Chip, IconButton, TextField, Typography, useTheme } from '@mui/material';
import CustomDialog from '@ui/components/common/Dialog';
import { StyledMenu } from '@ui/components/common/Menu';
import AddNewActivityTaskDialog from '@ui/pages/People/PersonDetail/components/FamilyOverview/AddNewActivityTaskDialog';
import { centeredRowStack } from '@ui/style/style';
import { ArrowRight, Calendar, MagicStar, UserTick, Task } from 'iconsax-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete } from 'react-iconly';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useActivityTaskCardStyles from './useActivityTaskCardStyles';
import { TaskType } from '@application/enums/task-type';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
const ActivityTaskCard = ({ heading, status, eventOrganizer, date, time, priority = 'medium', taskData, onStatusUpdate, hideMenu = false, showRating = true, userAssignedDisabled, taskCardSx, assignButtonSx, personId, familyMembers, showPersonIcon = false, taskType, relationId }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const styles = useActivityTaskCardStyles(priority, status);
    const dispatch = useAppDispatch();
    const userState = useAppSelector((state) => state.user);
    const peopleState = useAppSelector((state) => state.people);
    const [anchorUsersEl, setAnchorUsersEl] = useState(null);
    const [anchorMoreOptionEl, setAnchorMoreOptionEl] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    dayjs.extend(customParseFormat);
    const getPriorityTranslationKey = (priorityValue) => {
        switch (priorityValue) {
            case PriorityEnum.High:
                return 'priority.high';
            case PriorityEnum.Medium:
                return 'priority.medium';
            case PriorityEnum.Low:
                return 'priority.low';
            case PriorityEnum.None:
                return 'priority.none';
            default:
                return 'priority.medium';
        }
    };
    const getStatusInfo = () => {
        const defaultStatus = TaskCompletionStatusEnum.New;
        const currentStatus = status ?? defaultStatus;
        switch (currentStatus) {
            case TaskCompletionStatusEnum.New:
                return {
                    label: t('tasks.new', { defaultValue: 'New' }),
                    circleColor: theme.palette.text.primary,
                    textColor: theme.palette.text.primary
                };
            case TaskCompletionStatusEnum.InProgress:
                return {
                    label: t('tasks.inProgress', { defaultValue: 'In progress' }),
                    circleColor: theme.palette.primary.main,
                    textColor: theme.palette.primary.main
                };
            case TaskCompletionStatusEnum.Completed:
                return {
                    label: t('tasks.completed', { defaultValue: 'Completed' }),
                    circleColor: theme.palette.success.main,
                    textColor: theme.palette.success.main
                };
            default:
                return {
                    label: t('tasks.new', { defaultValue: 'New' }),
                    circleColor: theme.palette.text.primary,
                    textColor: theme.palette.text.primary
                };
        }
    };
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canUpdateTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Update');
    const canDeleteTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Delete');
    React.useEffect(() => {
        if (taskData?.assignedTo) {
            setSelectedUsers(taskData.assignedTo);
        }
        else {
            setSelectedUsers([]);
        }
    }, [taskData?.assignedTo]);
    const prevSelectedUsersLength = React.useRef(selectedUsers.length);
    React.useEffect(() => {
        const wasEmpty = prevSelectedUsersLength.current === 0;
        const isEmpty = selectedUsers.length === 0;
        if (wasEmpty !== isEmpty && anchorUsersEl) {
            setAnchorUsersEl(null);
        }
        prevSelectedUsersLength.current = selectedUsers.length;
    }, [selectedUsers.length]);
    React.useEffect(() => {
        if (!peopleState?.peopleForTasks) {
            dispatch(getAllPeopleForTasksAsync());
        }
    }, [dispatch, peopleState?.peopleForTasks]);
    const getPeopleActivityLabel = () => {
        if (taskType !== TaskType.People_Activity)
            return '';
        const basePersonId = personId || taskData?.personId;
        const baseRelationId = relationId || taskData?.relationId;
        const people = peopleState?.peopleForTasks;
        if (!people || !basePersonId)
            return 'People Activity';
        const person = people.find((p) => p.id === basePersonId);
        if (!person)
            return 'People Activity';
        if (baseRelationId) {
            const family = (person.familyMembers || []).find((fm) => fm.id === baseRelationId);
            if (family) {
                const relationText = family.relation ? ` (${family.relation} of ${person.name})` : '';
                return `${family.name}${relationText}`;
            }
        }
        return person.name;
    };
    const handleUserSelection = async (userId) => {
        if (!taskData || isUpdating)
            return;
        const newSelectedUsers = selectedUsers.includes(userId)
            ? selectedUsers.filter((id) => id !== userId)
            : [...selectedUsers, userId];
        const wasEmpty = selectedUsers.length === 0;
        const willBeEmpty = newSelectedUsers.length === 0;
        if (wasEmpty !== willBeEmpty && anchorUsersEl) {
            setAnchorUsersEl(null);
        }
        setSelectedUsers(newSelectedUsers);
        setIsUpdating(true);
        try {
            const updateRequest = {
                id: taskData.id,
                name: taskData.name,
                deadline: taskData.deadline,
                priority: taskData.priority,
                completionStatus: taskData.completionStatus,
                assignedTo: newSelectedUsers.length > 0 ? newSelectedUsers : null,
                eventId: taskData?.eventId || null,
                activityId: taskData?.activityId || null,
                personId: taskData?.personId || null,
                isActivityTask: taskData?.isActivityTask || false,
                relationId: taskData?.relationId || null,
                taskType: taskData.taskType,
                collaboratorIds: taskData?.collaboratorIds || null,
                CollaboratorTaskAccessLevel: taskData?.collaboratorTaskAccessLevel || undefined
            };
            await dispatch(updateTaskAsync(updateRequest)).unwrap();
            if (onStatusUpdate) {
                onStatusUpdate();
            }
            const user = userState.usersWithoutPagination.find((u) => u.id === userId);
            const userName = user ? `${user.firstName} ${user.lastName}` : 'Unknown';
            if (newSelectedUsers.includes(userId)) {
                toast.success(t('tasks.taskAssignedSuccessfully', { userName }) ||
                    `Task assigned to ${userName} successfully!`);
            }
            else {
                toast.success(t('tasks.taskUnassignedSuccessfully', { userName }) ||
                    `Task unassigned from ${userName} successfully!`);
            }
        }
        catch (error) {
            setSelectedUsers(selectedUsers);
        }
        finally {
            setIsUpdating(false);
        }
    };
    const getStatusActionText = () => {
        switch (status) {
            case TaskCompletionStatusEnum.New:
                return t('tasks.InProgress');
            case TaskCompletionStatusEnum.InProgress:
                return t('tasks.markAsComplete');
            case TaskCompletionStatusEnum.Completed:
                return t('tasks.InProgress');
            default:
                return t('tasks.markAsComplete');
        }
    };
    const getNextCompletionStatus = () => {
        switch (status) {
            case TaskCompletionStatusEnum.New:
                return TaskCompletionStatusEnum.InProgress;
            case TaskCompletionStatusEnum.InProgress:
                return TaskCompletionStatusEnum.Completed;
            case TaskCompletionStatusEnum.Completed:
                return TaskCompletionStatusEnum.InProgress;
            default:
                return TaskCompletionStatusEnum.InProgress;
        }
    };
    const handleStatusUpdate = async () => {
        if (!taskData || isUpdating)
            return;
        setIsUpdating(true);
        try {
            const updateRequest = {
                id: taskData.id,
                name: taskData.name,
                deadline: taskData.deadline,
                priority: taskData.priority,
                completionStatus: getNextCompletionStatus(),
                assignedTo: taskData.assignedTo || null,
                eventId: taskData.eventId || null,
                activityId: taskData.activityId || null,
                personId: taskData.personId || null,
                isActivityTask: taskData.isActivityTask || false,
                relationId: taskData.relationId || null,
                taskType: taskData.taskType,
                collaboratorIds: taskData?.collaboratorIds || null,
                CollaboratorTaskAccessLevel: taskData?.collaboratorTaskAccessLevel || undefined
            };
            await dispatch(updateTaskAsync(updateRequest)).unwrap();
            if (onStatusUpdate) {
                onStatusUpdate();
            }
            const nextStatus = getNextCompletionStatus();
            let statusMessage = '';
            switch (nextStatus) {
                case TaskCompletionStatusEnum.InProgress:
                    statusMessage = t('tasks.taskMarkedInProgress') || 'Task marked as in progress!';
                    break;
                case TaskCompletionStatusEnum.Completed:
                    statusMessage = t('tasks.taskMarkedCompleted') || 'Task marked as completed!';
                    break;
                default:
                    statusMessage = t('tasks.taskStatusUpdated') || 'Task status updated!';
            }
            toast.success(statusMessage);
        }
        catch (error) {
        }
        finally {
            setIsUpdating(false);
            setAnchorMoreOptionEl(null);
        }
    };
    const handleDeleteTask = async () => {
        if (!taskData || isUpdating)
            return;
        setIsUpdating(true);
        try {
            await dispatch(deleteTaskAsync(taskData.id)).unwrap();
            if (onStatusUpdate) {
                onStatusUpdate();
            }
            toast.success(t('tasks.taskDeletedSuccessfully') || 'Task deleted successfully!');
        }
        catch (error) {
        }
        finally {
            setIsUpdating(false);
            setAnchorMoreOptionEl(null);
            setOpenDeleteConfirmation(false);
        }
    };
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
    const getAssignedUsers = () => {
        if (!taskData?.assignedTo || taskData.assignedTo.length === 0)
            return [];
        return userState.usersWithoutPagination.filter((user) => taskData.assignedTo?.includes(user.id));
    };
    const assignedUsers = getAssignedUsers();
    const getUserInitials = (user) => {
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';
        return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
    };
    const userMenu = [
        ...userState.usersWithoutPagination
            .filter((user) => {
            const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
            return fullName.includes(userSearchTerm.toLowerCase());
        })
            .map((user) => ({
            text: `${user.firstName} ${user.lastName}`,
            to: () => handleUserSelection(user.id),
            selected: selectedUsers.includes(user.id),
            group: true,
            keepOpen: true,
            isCheckbox: true,
            icon: (_jsx(Checkbox, { size: 'small', checked: selectedUsers.includes(user.id), onChange: () => handleUserSelection(user.id) }))
        }))
    ];
    const moreMenuOptions = [
        {
            text: getStatusActionText(),
            to: handleStatusUpdate,
            disabled: isUpdating || (!canUpdateTasks && !canDeleteTasks),
            isCheckbox: true,
            icon: (_jsx(Checkbox, { size: 'small', disabled: isUpdating || (!canUpdateTasks && !canDeleteTasks), onClick: (e) => {
                    e.stopPropagation();
                    handleStatusUpdate();
                } }))
        },
        ...(status === TaskCompletionStatusEnum.New || status === TaskCompletionStatusEnum.InProgress
            ? [
                {
                    text: t('tasks.edit'),
                    group: true,
                    icon: _jsx(Icon, { icon: 'eva:edit-outline', fontSize: 20 }),
                    to: () => {
                        setIsEditTaskOpen(true);
                    },
                    disabled: !canUpdateTasks && !canDeleteTasks
                }
            ]
            : []),
        ...(status === TaskCompletionStatusEnum.New || status === TaskCompletionStatusEnum.InProgress
            ? [
                {
                    text: t('tasks.delete'),
                    group: true,
                    color: theme.palette.error.light,
                    icon: _jsx(Delete, { style: { width: 20, height: 20 } }),
                    to: () => setOpenDeleteConfirmation(true),
                    disabled: isUpdating || !canDeleteTasks
                }
            ]
            : [])
    ];
    return (_jsx(_Fragment, { children: _jsxs(Box, { className: 'card__task', sx: { ...styles.taskCard, ...taskCardSx }, children: [_jsxs(Box, { className: 'box__top', sx: { ...styles.boxTop }, children: [status !== undefined ? (_jsxs(_Fragment, { children: [(() => {
                                    const statusInfo = getStatusInfo();
                                    return (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.75, children: [_jsx(Box, { sx: {
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: statusInfo.circleColor,
                                                    flexShrink: 0
                                                } }), _jsx(Typography, { variant: 'body2', sx: {
                                                    color: statusInfo.textColor,
                                                    fontWeight: 500,
                                                    fontSize: '14px'
                                                }, children: statusInfo.label })] }));
                                })(), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [_jsx(Chip, { label: t(getPriorityTranslationKey(priority)), size: 'small', sx: styles.priorityChip }), !hideMenu && (_jsx(IconButton, { color: 'default', size: 'small', onClick: (e) => {
                                                e.stopPropagation();
                                                setAnchorMoreOptionEl(e.currentTarget);
                                            }, children: _jsx(Icon, { icon: 'solar:menu-dots-bold', color: theme.palette.text.primary }) }))] })] })) : (_jsxs(_Fragment, { children: [_jsx(Chip, { label: t(getPriorityTranslationKey(priority)), size: 'small', sx: styles.priorityChip }), _jsx(Box, { display: 'flex', alignItems: 'center', gap: 1, children: !hideMenu && (_jsx(IconButton, { color: 'default', size: 'small', onClick: (e) => {
                                            e.stopPropagation();
                                            setAnchorMoreOptionEl(e.currentTarget);
                                        }, children: _jsx(Icon, { icon: 'solar:menu-dots-bold', color: theme.palette.text.primary }) })) })] })), anchorUsersEl && (_jsx(StyledMenu, { anchorEl: anchorUsersEl, open: Boolean(anchorUsersEl), onClose: () => {
                                setAnchorUsersEl(null);
                                setUserSearchTerm('');
                            }, actions: userMenu, searchField: _jsx(Box, { sx: {
                                    px: 2,
                                    pb: 1,
                                    pt: 1,
                                    backgroundColor: theme.palette.background.paper,
                                    zIndex: 1,
                                    flexShrink: 0,
                                    position: 'sticky',
                                    top: 0
                                }, children: _jsx(TextField, { fullWidth: true, placeholder: 'Search users...', variant: 'outlined', size: 'small', value: userSearchTerm, onChange: (e) => {
                                        e.stopPropagation();
                                        setUserSearchTerm(e.target.value);
                                    }, onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), autoComplete: 'off', inputProps: {
                                        onBlur: (e) => {
                                            e.preventDefault();
                                            e.target.focus();
                                        }
                                    } }) }) }))] }), _jsx(Typography, { sx: { ...styles.taskHeading }, variant: 'body1', color: 'textPrimary', children: heading }), _jsxs(Box, { className: 'box__bottom', sx: { ...styles.boxBottom }, children: [showRating && (_jsxs(Box, { className: 'box__item', sx: { ...styles.boxItem, alignItems: 'flex-start' }, children: [_jsx(Box, { flexShrink: 0, display: 'flex', alignItems: 'flex-start', children: taskType === TaskType.People_Activity ? (_jsx(UserTick, { size: '20', color: theme.palette.grey[700] })) : taskType === TaskType.Event ? (_jsx(MagicStar, { size: '20', color: theme.palette.grey[700] })) : (_jsx(Task, { size: '20', color: theme.palette.grey[700] })) }), _jsx(Typography, { variant: 'body2', color: theme.palette.grey[700], sx: { lineHeight: 1.5, wordBreak: 'break-word', width: 'calc(100% - 70px)' }, children: taskType === TaskType.People_Activity
                                        ? getPeopleActivityLabel()
                                        : taskType === TaskType.Event
                                            ? taskData?.eventName || eventOrganizer || 'N/A'
                                            : t('tasks.others') })] })), showPersonIcon && (_jsxs(Box, { className: 'box__item', sx: { ...styles.boxItem, alignItems: 'flex-start' }, children: [_jsx(Box, { flexShrink: 0, display: 'flex', alignItems: 'flex-start', children: _jsx(UserTick, { size: '20', color: theme.palette.grey[700] }) }), _jsx(Typography, { variant: 'body2', color: theme.palette.grey[700], sx: { lineHeight: 1.5, wordBreak: 'break-word', width: 'calc(100% - 70px)' }, children: getPeopleActivityLabel() })] })), _jsxs(Box, { className: 'box__item', sx: { ...styles.boxItem }, children: [_jsx(Calendar, { size: '20', color: theme.palette.text.secondary }), _jsxs(Typography, { variant: 'body2', color: 'textSecondary', sx: { display: 'flex' }, children: [date, _jsx("span", { children: _jsx(ArrowRight, { size: 16, style: { marginTop: 3 } }) }), dayjs(time, ['h:mmA', 'hh:mm A']).format('HH:mm')] })] }), _jsxs(Box, { className: 'box__item', sx: {
                                ...styles.boxItem,
                                width: '100%',
                                borderTop: `1px solid ${theme.palette.divider}`,
                                justifyContent: 'space-between',
                                gap: 1
                            }, children: [selectedUsers.length > 0 ? (_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: '7px',
                                        cursor: isUpdating || userAssignedDisabled || !canUpdateTasks ? 'default' : 'pointer',
                                        overflow: 'visible'
                                    }, "data-prevent-task-card-click": 'true', onClick: (e) => {
                                        if (isUpdating || userAssignedDisabled || !canUpdateTasks)
                                            return;
                                        e.stopPropagation();
                                        setAnchorUsersEl(e.currentTarget);
                                    }, children: [assignedUsers.slice(0, 3).map((user, idx) => (_jsx(Avatar, { src: user.profilePictureUrl &&
                                                user.profilePictureUrl !== '/placeholder.svg?height=24&width=24'
                                                ? user.profilePictureUrl
                                                : undefined, alt: `${user.firstName || ''} ${user.lastName || ''}`, sx: {
                                                width: 32,
                                                height: 32,
                                                ml: idx === 0 ? 0 : '-8px',
                                                border: `2px solid ${theme.palette.background.paper}`,
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                borderRadius: 1.5,
                                                zIndex: 3 - idx,
                                                backgroundColor: theme.palette.background.neutral,
                                                color: theme.palette.action.active
                                            }, children: getUserInitials(user) }, user.id || idx))), assignedUsers.length > 3 && (_jsxs(Avatar, { sx: {
                                                width: 32,
                                                height: 32,
                                                ml: '-8px',
                                                fontSize: '12px',
                                                borderRadius: 1.5,
                                                fontWeight: 500,
                                                border: `2px solid ${theme.palette.background.paper}`,
                                                backgroundColor: theme.palette.primary.lighter,
                                                color: theme.palette.primary.dark,
                                                zIndex: 0
                                            }, children: ["+", assignedUsers.length - 3] }))] })) : (_jsx(Button, { variant: 'outlined', size: 'small', endIcon: _jsx(ArrowRight, { size: 18 }), sx: {
                                        ...styles.assignToDropdown,
                                        color: theme.palette.primary.darker,
                                        textTransform: 'none',
                                        fontWeight: 400,
                                        '&:hover': {
                                            backgroundColor: theme.palette.grey[50],
                                            borderColor: theme.palette.primary.darker
                                        },
                                        ...assignButtonSx
                                    }, "data-prevent-task-card-click": 'true', onClick: (e) => {
                                        setAnchorUsersEl(e.currentTarget);
                                    }, disabled: isUpdating || userAssignedDisabled || !canUpdateTasks, children: _jsx(Typography, { noWrap: true, sx: {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontSize: '13px',
                                            maxWidth: { xs: '200px', lg: '100%' }
                                        }, children: t('tasks.assignto') }) })), anchorMoreOptionEl && (_jsx(StyledMenu, { anchorEl: anchorMoreOptionEl, open: Boolean(anchorMoreOptionEl), onClose: () => {
                                        setAnchorMoreOptionEl(null);
                                    }, actions: moreMenuOptions }))] })] }), isEditTaskOpen && (_jsx(AddNewActivityTaskDialog, { onClose: () => setIsEditTaskOpen(false), open: isEditTaskOpen, title: t('people.editActivityTask'), editMode: true, taskData: taskData, personId: personId, familyMembers: familyMembers, onSuccess: () => {
                        if (onStatusUpdate) {
                            onStatusUpdate();
                        }
                    } })), openDeleteConfirmation && (_jsx(CustomDialog, { open: openDeleteConfirmation, title: t('common.deleteConfirmation'), onClose: () => setOpenDeleteConfirmation(false), actions: _jsx(_Fragment, { children: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', onClick: () => setOpenDeleteConfirmation(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'error', fullWidth: true, disabled: !canDeleteTasks || isUpdating, onClick: () => {
                                        handleDeleteTask();
                                    }, children: t('actions.deleteNow') })] }) }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { children: t('common.deleteWarningMessage') }) }) }))] }) }));
};
export default ActivityTaskCard;
